import { getDataSource } from '@/lib/data-source';
import { OneTimePaymentPlan, MultiCurrencyPrice, MultiLanguageString, MultiLanguageStringArray, TranslatedOneTimePlan, TranslationParams } from '@/types/billing';

// Request/Response Types matching JSON:API format
export interface OneTimePlanAttributes {
  name: string;
  nameTranslations?: MultiLanguageString;
  description?: string;
  descriptionTranslations?: MultiLanguageString;
  type: 'individual' | 'teams';
  prices: MultiCurrencyPrice;
  defaultCurrency?: string;
  accessDurationMonths: number | 'unlimited';
  status: 'draft' | 'active' | 'archived';
  includedSeats?: number; // For teams type
  createdAt?: string;
  updatedAt?: string;
}

export interface OneTimePlanFeaturesData {
  features: string[];
  featuresTranslations?: MultiLanguageStringArray;
}

export interface OneTimePlanCreateData {
  name: string;
  description?: string;
  type: 'individual' | 'teams';
  prices: MultiCurrencyPrice;
  priceIds?: { [currency: string]: string }; // For including existing price IDs in updates
  // New selective pricing structure
  pricing?: {
    prices: MultiCurrencyPrice;
    priceIds: { [currency: string]: string };
  };
  defaultCurrency?: string;
  accessDurationMonths: number | 'unlimited';
  includedSeats?: number; // Required for teams
  pricesPerAdditionalSeat?: MultiCurrencyPrice; // For teams
  status?: 'draft' | 'active' | 'archived';
  features?: string[];
  featuresTranslations?: MultiLanguageStringArray;
  featureFlags?: { [key: string]: boolean };
  isRecommended?: boolean;
  offerFreeTrial?: boolean;
  freeTrialDays?: number;
  translations?: {
    name?: MultiLanguageString;
    description?: MultiLanguageString;
    features?: MultiLanguageStringArray;
  };
}

export interface OneTimePlanUpdateData extends Partial<OneTimePlanCreateData> {}

export interface OneTimePlanFilters {
  type?: 'individual' | 'teams';
  status?: 'active' | 'draft' | 'archived';
  accessType?: 'unlimited' | 'time-limited';
}

export interface OneTimePlanSortOptions {
  field?: 'name' | '-name' | 'access-duration' | '-access-duration' | '-created-at';
}

export interface OneTimePlanListResponse {
  data: OneTimePaymentPlan[];
  meta?: {
    pagination?: {
      total: number;
      count: number;
      perPage: number;
      currentPage: number;
      totalPages: number;
    };
  };
  jsonapi: {
    version: string;
  };
}

class OneTimePlansService {
  private dataSource = getDataSource();

  /**
   * Format translation parameters for URL query string
   */
  private formatTranslationParams(params?: TranslationParams): URLSearchParams {
    const urlParams = new URLSearchParams();
    
    if (params?.lang) {
      urlParams.append('lang', params.lang);
    }
    
    if (params?.includeTranslations !== undefined) {
      if (typeof params.includeTranslations === 'boolean') {
        urlParams.append('include_translations', params.includeTranslations ? 'true' : 'false');
      } else if (typeof params.includeTranslations === 'string') {
        urlParams.append('include_translations', params.includeTranslations);
      } else if (Array.isArray(params.includeTranslations)) {
        urlParams.append('include_translations', params.includeTranslations.join(','));
      }
    }
    
    return urlParams;
  }


  /**
   * Get all one-time plans for a product
   */
  async getOneTimePlans(
    productId: string,
    filters?: OneTimePlanFilters,
    sort?: OneTimePlanSortOptions,
    translationParams?: TranslationParams
  ): Promise<TranslatedOneTimePlan[]> {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters?.type) params.append('filter[type]', filters.type);
      if (filters?.status) params.append('filter[status]', filters.status);
      if (filters?.accessType) {
        params.append('filter[access_type]', filters.accessType);
      }
      
      // Add sorting
      if (sort?.field) params.append('sort', sort.field);
      
      // Add include parameter to get price IDs
      params.append('include', 'prices');
      
      // Add translation parameters
      const translationUrlParams = this.formatTranslationParams(translationParams);
      translationUrlParams.forEach((value, key) => {
        params.append(key, value);
      });
      
      const queryString = params.toString();
      const url = `/api/v1/products/${productId}/billing/one-time-plans${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.dataSource.get<OneTimePlanListResponse>(url);
      
      console.log('Raw API response:', response.data);
      
      // The LiveAdapter already deserializes JSON:API, so response.data should already be transformed
      // We just need to extract the data array and apply our custom transformations
      const responseData = response.data as unknown as Record<string, unknown>;
      
      // Return the data array
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const plans = (responseData as { data: TranslatedOneTimePlan[] }).data;
        console.log('Extracted plans array:', plans);
        
        // Transform is_lifetime to accessDurationMonths: 'unlimited' and handle prices
        return plans.map(plan => {
          console.log('OneTimePlansService - Before transformation:', plan);
          
          // Transform is_lifetime to accessDurationMonths: 'unlimited'
          if (plan.isLifetime === true) {
            plan.accessDurationMonths = 'unlimited';
            console.log('OneTimePlansService - Transformed to unlimited');
          }
          
          // Transform pricesArray to prices object if needed
          if (plan.pricesArray && Array.isArray(plan.pricesArray)) {
            const pricesObj: Record<string, number> = {};
            plan.pricesArray.forEach((price: Record<string, unknown>) => {
              if (price.attributes && typeof price.attributes === 'object') {
                // JSON:API format
                const attrs = price.attributes as Record<string, unknown>;
                if (attrs.currency && typeof attrs.currency === 'string' && typeof attrs.basePrice === 'number') {
                  pricesObj[attrs.currency] = attrs.basePrice;
                }
              } else if (typeof price.currency === 'string' && typeof price.basePrice === 'number') {
                // Direct format
                pricesObj[price.currency] = price.basePrice;
              }
            });
            plan.prices = pricesObj;
            console.log('OneTimePlansService - Transformed prices:', plan.prices);
          }
          
          // Transform featuresArray to features array if needed
          if (plan.featuresArray && Array.isArray(plan.featuresArray)) {
            const featuresArray: string[] = [];
            plan.featuresArray.forEach((feature: Record<string, unknown>) => {
              if (feature.attributes && typeof feature.attributes === 'object') {
                // JSON:API format
                const attrs = feature.attributes as Record<string, unknown>;
                if (attrs.name && typeof attrs.name === 'string') {
                  featuresArray.push(attrs.name);
                }
              } else if (typeof feature.name === 'string') {
                // Direct format
                featuresArray.push(feature.name);
              }
            });
            plan.features = featuresArray;
            console.log('OneTimePlansService - Transformed features:', plan.features);
          }
          
          console.log('OneTimePlansService - After transformation:', plan);
          return plan;
        });
      }
      
      return [];
    } catch (error) {
      console.error(`Error fetching one-time plans for product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Get a single one-time plan
   */
  async getOneTimePlan(
    productId: string, 
    planId: string,
    translationParams?: TranslationParams
  ): Promise<TranslatedOneTimePlan> {
    try {
      const params = this.formatTranslationParams(translationParams);
      // Add include parameter to get price IDs
      params.append('include', 'prices');
      const queryString = params.toString();
      const url = `/api/v1/products/${productId}/billing/one-time-plans/${planId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.dataSource.get<{ data: TranslatedOneTimePlan }>(url);
      
      console.log('OneTimePlansService - Raw API response (get):', response.data);
      
      // Handle both wrapped and unwrapped response formats
      const responseData = response.data as unknown;
      let plan: TranslatedOneTimePlan;
      
      // Check if response is wrapped in a data property
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        plan = (responseData as { data: TranslatedOneTimePlan }).data;
      } else {
        plan = responseData as OneTimePaymentPlan;
      }
      
      if (plan && typeof plan === 'object' && plan.id) {
        // Transform is_lifetime to accessDurationMonths: 'unlimited'
        if (plan.isLifetime === true) {
          plan.accessDurationMonths = 'unlimited';
        }
        
        console.log('OneTimePlansService - Transformed plan (get):', plan);
        return plan;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error fetching one-time plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new one-time plan
   */
  async createOneTimePlan(
    productId: string,
    data: OneTimePlanCreateData
  ): Promise<OneTimePaymentPlan> {
    try {
      // Use selective pricing structure if available, otherwise fallback to legacy structure
      let pricesToUse: MultiCurrencyPrice;
      let priceIdsToUse: { [currency: string]: string } = {};
      
      if (data.pricing) {
        // New selective pricing structure
        pricesToUse = data.pricing.prices;
        priceIdsToUse = data.pricing.priceIds;
        console.log('OneTimePlansService - Using new selective pricing structure');
        console.log('OneTimePlansService - Selective prices:', pricesToUse);
        console.log('OneTimePlansService - Selective price IDs:', priceIdsToUse);
      } else {
        // Legacy pricing structure
        pricesToUse = data.prices;
        priceIdsToUse = data.priceIds || {};
        console.log('OneTimePlansService - Using legacy pricing structure');
      }

      // Convert MultiCurrencyPrice object to array format for the API
      const pricesArray = Object.entries(pricesToUse).map(([currency, amount]) => ({
        currency,
        amount,
        // Include price ID if available (for updates)
        ...(priceIdsToUse[currency] && { id: priceIdsToUse[currency] })
      }));
      console.log('OneTimePlansService - createOneTimePlan pricesArray:', pricesArray);

      // Prepare the request body in JSON:API format
      const requestBody = {
        data: {
          type: 'one-time-plans',
          attributes: {
            name: data.name,
            description: data.description,
            type: data.type,
            prices: pricesArray, // Send as array with potential IDs
            'default-currency': data.defaultCurrency,
            'access-duration-months': data.accessDurationMonths,
            'included-seats': data.includedSeats,
            'prices-per-additional-seat': data.pricesPerAdditionalSeat,
            status: data.status || 'draft',
            ...(data.features && { features: data.features }),
            ...(data.featuresTranslations && { 'features-translations': data.featuresTranslations }),
            ...(data.featureFlags && { 'feature-flags': data.featureFlags }),
            ...(data.isRecommended !== undefined && { 'is-recommended': data.isRecommended }),
            ...(data.offerFreeTrial !== undefined && { 'offer-free-trial': data.offerFreeTrial }),
            ...(data.freeTrialDays !== undefined && { 'free-trial-days': data.freeTrialDays }),
            ...(data.translations && { translations: data.translations })
          }
        }
      };

      const response = await this.dataSource.post<{ data: OneTimePaymentPlan }>(
        `/api/v1/products/${productId}/billing/one-time-plans`,
        requestBody
      );
      
      console.log('OneTimePlansService - Raw API response (create):', response.data);
      
      // Handle both wrapped and unwrapped response formats
      const responseData = response.data as unknown;
      let plan: OneTimePaymentPlan;
      
      // Check if response is wrapped in a data property
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        plan = (responseData as { data: OneTimePaymentPlan }).data;
      } else {
        plan = responseData as OneTimePaymentPlan;
      }
      
      if (plan && typeof plan === 'object' && plan.id) {
        // Transform is_lifetime to accessDurationMonths: 'unlimited'
        if (plan.isLifetime === true) {
          plan.accessDurationMonths = 'unlimited';
        }
        
        console.log('OneTimePlansService - Transformed plan (create):', plan);
        return plan;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error creating one-time plan:`, error);
      throw error;
    }
  }

  /**
   * Update a one-time plan (full update)
   */
  async updateOneTimePlan(
    productId: string,
    planId: string,
    data: OneTimePlanUpdateData
  ): Promise<OneTimePaymentPlan> {
    try {
      console.log('OneTimePlansService - updateOneTimePlan called with data:', data);
      console.log('OneTimePlansService - Plan type in data:', data.type);
      
      // Use selective pricing structure if available, otherwise fallback to legacy structure
      let pricesToUse: MultiCurrencyPrice | undefined;
      let priceIdsToUse: { [currency: string]: string } = {};
      
      if (data.pricing) {
        // New selective pricing structure
        pricesToUse = data.pricing.prices;
        priceIdsToUse = data.pricing.priceIds;
        console.log('OneTimePlansService - updateOneTimePlan using new selective pricing structure');
        console.log('OneTimePlansService - Selective prices:', pricesToUse);
        console.log('OneTimePlansService - Selective price IDs:', priceIdsToUse);
      } else if (data.prices) {
        // Legacy pricing structure
        pricesToUse = data.prices;
        priceIdsToUse = data.priceIds || {};
        console.log('OneTimePlansService - updateOneTimePlan using legacy pricing structure');
      }

      // Convert MultiCurrencyPrice object to array format for the API if prices are provided
      const pricesArray = pricesToUse ? Object.entries(pricesToUse).map(([currency, amount]) => ({
        currency,
        amount,
        // Include price ID if available (for updates)
        ...(priceIdsToUse[currency] && { id: priceIdsToUse[currency] })
      })) : undefined;
      console.log('OneTimePlansService - updateOneTimePlan pricesArray:', pricesArray);
      
      const requestBody = {
        data: {
          type: 'one-time-plans',
          id: planId,
          attributes: {
            ...(data.name && { name: data.name }),
            ...(data.description !== undefined && { description: data.description }),
            ...(data.type && { type: data.type }), // Make sure to include type in the update
            ...(pricesArray && { prices: pricesArray }), // Send as array with potential IDs
            ...(data.defaultCurrency && { 'default-currency': data.defaultCurrency }),
            ...(data.accessDurationMonths !== undefined && { 'access-duration-months': data.accessDurationMonths }),
            ...(data.status && { status: data.status }),
            ...(data.includedSeats !== undefined && { 'included-seats': data.includedSeats }),
            ...(data.pricesPerAdditionalSeat && { 'prices-per-additional-seat': data.pricesPerAdditionalSeat }),
            ...(data.features && { features: data.features }),
            ...(data.featuresTranslations && { 'features-translations': data.featuresTranslations }),
            ...(data.featureFlags && { 'feature-flags': data.featureFlags }),
            ...(data.isRecommended !== undefined && { 'is-recommended': data.isRecommended }),
            ...(data.offerFreeTrial !== undefined && { 'offer-free-trial': data.offerFreeTrial }),
            ...(data.freeTrialDays !== undefined && { 'free-trial-days': data.freeTrialDays }),
            ...(data.translations && { translations: data.translations })
          }
        }
      };
      
      console.log('OneTimePlansService - Request body attributes:', requestBody.data.attributes);

      const response = await this.dataSource.put<{ data: OneTimePaymentPlan }>(
        `/api/v1/products/${productId}/billing/one-time-plans/${planId}`,
        requestBody
      );
      
      console.log('OneTimePlansService - Raw API response:', response.data);
      
      // Handle both wrapped and unwrapped response formats
      const responseData = response.data as unknown;
      let plan: OneTimePaymentPlan;
      
      // Check if response is wrapped in a data property
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        plan = (responseData as { data: OneTimePaymentPlan }).data;
      } else {
        plan = responseData as OneTimePaymentPlan;
      }
      
      if (plan && typeof plan === 'object' && plan.id) {
        // Transform is_lifetime to accessDurationMonths: 'unlimited'
        if (plan.isLifetime === true) {
          plan.accessDurationMonths = 'unlimited';
        }
        
        console.log('OneTimePlansService - Transformed plan:', plan);
        return plan;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error updating one-time plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Partially update a one-time plan
   */
  async patchOneTimePlan(
    productId: string,
    planId: string,
    data: Partial<OneTimePlanUpdateData>
  ): Promise<OneTimePaymentPlan> {
    try {
      const requestBody = {
        data: {
          type: 'one-time-plans',
          id: planId,
          attributes: {
            ...(data.status && { status: data.status }),
            ...(data.name && { name: data.name }),
            ...(data.accessDurationMonths !== undefined && { 'access-duration-months': data.accessDurationMonths })
          }
        }
      };

      const response = await this.dataSource.patch<{ data: OneTimePaymentPlan }>(
        `/api/v1/products/${productId}/billing/one-time-plans/${planId}`,
        requestBody
      );
      
      console.log('OneTimePlansService - Raw API response (patch):', response.data);
      
      // Handle both wrapped and unwrapped response formats
      const responseData = response.data as unknown;
      let plan: OneTimePaymentPlan;
      
      // Check if response is wrapped in a data property
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        plan = (responseData as { data: OneTimePaymentPlan }).data;
      } else {
        plan = responseData as OneTimePaymentPlan;
      }
      
      if (plan && typeof plan === 'object' && plan.id) {
        // Transform is_lifetime to accessDurationMonths: 'unlimited'
        if (plan.isLifetime === true) {
          plan.accessDurationMonths = 'unlimited';
        }
        
        console.log('OneTimePlansService - Transformed plan (patch):', plan);
        return plan;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error patching one-time plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a one-time plan
   */
  async deleteOneTimePlan(productId: string, planId: string): Promise<void> {
    try {
      await this.dataSource.delete(
        `/api/v1/products/${productId}/billing/one-time-plans/${planId}`
      );
    } catch (error) {
      console.error(`Error deleting one-time plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Archive a one-time plan (safer than delete)
   */
  async archiveOneTimePlan(productId: string, planId: string): Promise<OneTimePaymentPlan> {
    return this.patchOneTimePlan(productId, planId, { status: 'archived' });
  }

  /**
   * Calculate the effective monthly price for a one-time plan
   */
  calculateMonthlyEquivalent(price: number, durationMonths: number | 'unlimited'): number | null {
    if (durationMonths === 'unlimited') return null;
    return Number((price / durationMonths).toFixed(2));
  }

  /**
   * Validate access duration
   */
  validateAccessDuration(duration: number | 'unlimited'): boolean {
    if (duration === 'unlimited') return true;
    return typeof duration === 'number' && duration > 0 && duration <= 120; // Max 10 years
  }

  /**
   * Format access duration for display
   */
  formatAccessDuration(duration: number | 'unlimited'): string {
    if (duration === 'unlimited') return 'Lifetime access';
    if (duration === 1) return '1 month';
    if (duration === 12) return '1 year';
    if (duration === 24) return '2 years';
    if (duration < 12) return `${duration} months`;
    const years = Math.floor(duration / 12);
    const months = duration % 12;
    if (months === 0) return `${years} year${years > 1 ? 's' : ''}`;
    return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
  }
}

export const oneTimePlansService = new OneTimePlansService();